import to from 'await-to-js';
import {isMatch} from 'lodash';
import {connect} from 'react-redux';
import {container} from 'op/container';
import {compose, withProps, withHandlers, withState, lifecycle} from 'recompose';

import {withAxios} from 'op/components/auth';
import {withTypes, withKpiTypes, withExecutionLevels} from 'op/components/lut';

export const withReportEvaluation = compose(
    withAxios,
    withTypes,
    withKpiTypes,
    withState('ready', 'setReady', false),
    withState('orderModel', 'setOrderModel', {column: null, direction: 'descending'}),
    withState('evaluations', 'setEvaluations', {}),
    withProps(({report, kpiTypes, types}) => ({
        reportType: types.find(type => report.type.id == type.id),
        kpiTypes: kpiTypes[report.category] || []
    })),
    withExecutionLevels('reportType.id'),
    withHandlers({
        getExecutionLevelOptions: ({reportType, executionLevels}) => () => {
            return executionLevels.map(({id, name}) => ({key: name, value: id}));
        }
    }),
    connect(({auth}) => ({
        user: auth.user,
    })),
    withProps(({evaluations, orderModel, setOrderModel}) => ({
        sorting: {
            ...orderModel,
            sorter: (a, b) => {
                switch (orderModel.column) {
                    case 'kpi':
                        if (orderModel.direction === 'ascending')
                            return a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1;
                        else
                            return a.name.toUpperCase() < b.name.toUpperCase() ? 1 : -1;
                    case 'value':
                        if (orderModel.direction === 'ascending')
                            return (b.id in evaluations ? evaluations[b.id].value : 100) - (a.id in evaluations ? evaluations[a.id].value : 100);
                        else
                            return (a.id in evaluations ? evaluations[a.id].value : 100) - (b.id in evaluations ? evaluations[b.id].value : 100);
                    default:
                        if (a.order || b.order)
                            return a.order - b.order;
                        else
                            return a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1;
                }
            },
            sortBy: (column) => {
                if (column === orderModel.column && orderModel.direction === 'descending')
                    setOrderModel({...orderModel, column: null});
                else
                    setOrderModel({
                        column,
                        direction: orderModel.direction === 'ascending' ? 'descending' : 'ascending'
                    });
            }
        },
    })),
    lifecycle({
        async componentDidMount() {
            const {ready, report, fetchKpiTypes, executionLevels, fetchExecutionLevels, types, fetchTypes, setReady, online} = this.props;
            if (online && !ready) {
                await fetchKpiTypes(report.category);
                if (types.length == 0)
                    await fetchTypes();
                if (executionLevels.length == 0)
                    await fetchExecutionLevels();
                setReady(true);
            }
        },
        async componentDidUpdate({report: prevProps}) {
            const {report, reportType, evaluations, setEvaluations, kpiTypes, fetchKpiTypes, setReady, online, invokeChange} = this.props;

            if (online && prevProps && (report.id !== prevProps.id || report.type.id !== prevProps.type.id)) {
                setReady(false);
                await fetchKpiTypes(report.category);
                if (executionLevels.length == 0)
                    await fetchExecutionLevels();
                setReady(true);
            }

            if (report && reportType && kpiTypes.length > 0) {
                // Get available KPIs that aren't expired
                const availableKpis = kpiTypes.filter(x => !x.isexpired);
                
                // Check if report.evaluations needs to be populated with default values
                const existingKpiIds = report.evaluations.map(evaluation => evaluation.type.id);
                const missingKpis = availableKpis.filter(kpi => !existingKpiIds.includes(kpi.id));
                
                // If there are missing KPIs, add them to report.evaluations with default values
                if (missingKpis.length > 0 && invokeChange) {
                    const defaultEvaluations = missingKpis.map(kpi => ({
                        report_id: report.id,
                        report_type: report.type.id,
                        person_id: report.hidden_person_id,
                        type: kpi,
                        execution_level: null, // Will be converted to 20 in transformer
                        score: null,
                        quantity: null,
                        remarks: '',
                        alert: false
                    }));
                    
                    // Add the missing evaluations to the report and mark as dirty
                    invokeChange({
                        evaluations: { $push: defaultEvaluations }
                    });
                }

                // Create UI state evaluation objects (now includes all KPIs)
                const nextEvals = report.evaluations
                    .reduce((acc, evaluation) => ({
                        ...acc,
                        [evaluation.type.id]: {
                            type: reportType.type,
                            value: evaluation[reportType.type],
                            remarks: evaluation.remarks,
                            alert: evaluation.alert === true
                        }
                    }), {});

                if (!isMatch(evaluations, nextEvals))
                    setEvaluations(nextEvals);
            }
        }
    }),
);
